const ABSENCE_TYPE_KEYS = ["bolovanje", "sluzbeni_put", "godisnji_odmor", "ostalo"];

const ABSENCE_LABEL_TO_KEY = {
    Bolovanje: "bolovanje",
    "Službeni put": "sluzbeni_put",
    "Sluzbeni put": "sluzbeni_put",
    "Godišnji odmor": "godisnji_odmor",
    "Godisnji odmor": "godisnji_odmor",
    Ostalo: "ostalo",
    "Sick leave": "bolovanje",
    "Business trip": "sluzbeni_put",
    "Annual leave": "godisnji_odmor",
    Other: "ostalo",
    Odsustvo: "fallback",
    Absence: "fallback",
};

const resolvePersonName = (notification, data, employeesData, t) => {
    if (data.requestingUserName) return data.requestingUserName;
    if (data.approverName) return data.approverName;
    if (data.ime || data.prezime) {
        return `${data.ime || ""} ${data.prezime || ""}`.trim();
    }

    const userId = data.requestingUserId || notification.relatedUserId;
    const employee = userId ? employeesData?.[userId] : null;
    if (employee) {
        return `${employee.ime || ""} ${employee.prezime || ""}`.trim();
    }

    return t("unknownUser");
};

const resolveAbsenceTypeLabel = (absenceType, t) => {
    if (!absenceType) return t("absenceTypes.fallback");

    if (ABSENCE_TYPE_KEYS.includes(absenceType)) {
        return t(`absenceTypes.${absenceType}`);
    }

    const mappedKey = ABSENCE_LABEL_TO_KEY[absenceType];
    if (mappedKey && mappedKey !== "fallback") {
        return t(`absenceTypes.${mappedKey}`);
    }
    if (mappedKey === "fallback") {
        return t("absenceTypes.fallback");
    }

    return absenceType;
};

const resolveDateRange = (data, t) => {
    if (data?.dateStart?.day != null && data?.dateStart?.month != null && data?.dateStart?.year != null) {
        const start = `${data.dateStart.day}.${data.dateStart.month}.${data.dateStart.year}`;
        const end = data.dateEnd;

        if (!end || end.day == null || end.month == null || end.year == null) {
            return `${start} - ${t("indefinite")}`;
        }

        if (
            data.dateStart.day === end.day
            && data.dateStart.month === end.month
            && data.dateStart.year === end.year
        ) {
            return start;
        }

        return `${start} - ${end.day}.${end.month}.${end.year}`;
    }

    if (!data?.dateRange) return "-";
    return String(data.dateRange)
        .replace(/\bneodredjeno\b/gi, t("indefinite"))
        .replace(/\bneodređeno\b/gi, t("indefinite"))
        .replace(/\bindefinite\b/gi, t("indefinite"));
};

const resolveEntranceKind = (data, fallbackMessage) => {
    if (data.entranceType === "online" || data.entranceType === "remote") return "online";
    if (data.entranceType === "tag" || data.entranceType === "office") return "tag";

    const message = String(fallbackMessage || "").toLowerCase();
    if (message.includes("online")) return "online";
    if (message.includes("tag")) return "tag";
    return "generic";
};

const resolveLogoutHour = (data) => {
    if (data.logoutHour != null) return String(data.logoutHour);
    if (typeof data.logoutTime === "string" && data.logoutTime.includes(":")) {
        return data.logoutTime.split(":")[0];
    }
    return "00";
};

/**
 * Compose localized notification title/message from type + relatedData.
 * Falls back to stored title/message for unknown/incomplete records.
 */
export const getNotificationCopy = (notification, t, { employeesData } = {}) => {
    const data = notification?.relatedData || {};
    const type = notification?.type;
    const fallback = {
        title: notification?.title || "",
        message: notification?.message || "",
    };

    if (!type) return fallback;

    try {
        switch (type) {
            case "employee_login": {
                const name = resolvePersonName(notification, data, employeesData, t);
                const time = data.time || "-";
                if (!data.ime && !data.prezime && !name) return fallback;

                const kind = resolveEntranceKind(data, notification.message);
                return {
                    title: t("employee_login.title"),
                    message: t(`employee_login.${kind}`, { name, time }),
                };
            }

            case "auto_logout": {
                const hour = resolveLogoutHour(data);
                const exitTime = data.exitTime || data.logoutTime || "-";
                return {
                    title: t("auto_logout.title", { hour }),
                    message: t("auto_logout.message", { exitTime }),
                };
            }

            case "absence_request": {
                const name = resolvePersonName(notification, data, employeesData, t);
                return {
                    title: t("absence_request.title"),
                    message: t("absence_request.message", {
                        name,
                        absenceType: resolveAbsenceTypeLabel(data.absenceType, t),
                        dateRange: resolveDateRange(data, t),
                    }),
                };
            }

            case "absence_approved": {
                return {
                    title: t("absence_approved.title"),
                    message: t("absence_approved.message", {
                        absenceType: resolveAbsenceTypeLabel(data.absenceType, t),
                        dateRange: resolveDateRange(data, t),
                    }),
                };
            }

            case "absence_rejected": {
                return {
                    title: t("absence_rejected.title"),
                    message: t("absence_rejected.message", {
                        absenceType: resolveAbsenceTypeLabel(data.absenceType, t),
                        dateRange: resolveDateRange(data, t),
                    }),
                };
            }

            case "absence_request_created": {
                const name = resolvePersonName(
                    notification,
                    { ...data, requestingUserName: data.approverName },
                    employeesData,
                    t
                );
                return {
                    title: t("absence_request_created.title"),
                    message: t("absence_request_created.message", {
                        name,
                        absenceType: resolveAbsenceTypeLabel(data.absenceType, t),
                        dateRange: resolveDateRange(data, t),
                    }),
                };
            }

            case "shift_confirmation_request": {
                const name = resolvePersonName(notification, data, employeesData, t);
                const presence = data.presenceType === "office"
                    ? t("presence.office")
                    : t("presence.remote");
                const time = data.entranceTime;

                return {
                    title: t("shift_confirmation_request.title"),
                    message: time
                        ? t("shift_confirmation_request.withTime", { name, presence, time })
                        : t("shift_confirmation_request.withoutTime", { name, presence }),
                };
            }

            default:
                return fallback;
        }
    } catch {
        return fallback;
    }
};
